import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Paper,
  Tooltip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Switch, 
  TextField
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import RefreshIcon from "@mui/icons-material/Refresh";
import LoadImagePreview from "./LoadImagePreview";

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import { Row, Col, Form, Button, Input, InputGroup } from "reactstrap";
import ComponentCard from "./ComponentCard";

import {
  startEC2API,
  stopEC2API,
  triggerDownloadForInferenceToEC2,
  checkEC2StatusAPI,
  fetchFilesAPI,
  getPresignedUrlAPI,
  triggerInferenceFileWatcherAPI,
  startDetectionScriptAPI,
  listModelsAPI,
  stopDetectionScriptAPI,
  orchestrateKVSStackAPI,
  listReadyPairsAPI
} from "../../service/ModelTestingService";

import pLimit from "p-limit";
import buildDynamicFileBatches from "./batchBuilder";
import { apiClient } from "../../service/ApiClient";

const authService = require("../../service/AuthService");

const ModelTestingComp = () => {
  const user = authService.getUser();
  const organization = user.organization.toLowerCase();
  const username = user.name.toLowerCase();

  // EC2 / Script state
  const [ec2Status, setEc2Status] = useState("");
  const [infScriptStatus, setInfScriptStatus] = useState("");
  const [startingEC2, setStartingEC2] = useState(false);
  const [stoppingEC2, setStoppingEC2] = useState(false);
  const [startingScript, setStartingScript] = useState(false);
  const [stoppingScript, setStoppingScript] = useState(false);
  const [selectedUseCase, setSelectedUseCase] = useState("");
  const [userTrainedModels, setUserTrainedModels] = useState([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [modelsError, setModelsError] = useState("");

  const [checkboxAccepted, setCheckboxAccepted] = useState(false);
  const [acceptedConfirmed, setAcceptedConfirmed] = useState(false);
  const [showCheckbox, setShowCheckbox] = useState(true);

  // Uploads (new run)
  const [selectedFiles, setSelectedFiles] = useState(null);
  const [uploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedCount, setUploadedCount] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [waitingForInference, setWaitingForInference] = useState(false);
  const [inferenceStatusMessage, setInferenceStatusMessage] = useState("");
  const [inferenceResults, setInferenceResults] = useState({}); // filename -> presigned URL
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadedPreviewUrl, setUploadedPreviewUrl] = useState("");
  const [showFileList, setShowFileList] = useState(false);
  const [inferenceMode, setInferenceMode] = useState("batch"); // 'batch' | 'streaming'
  const isStreaming = inferenceMode === "streaming";
  const [streamingBucket, setStreamingBucket] = useState("");
  const [streamingRunning, setStreamingRunning] = useState(false);
  const [startingStreaming, setStartingStreaming] = useState(false);
  const [stoppingStreaming, setStoppingStreaming] = useState(false);

  // Archive preview
  const [filePairs, setFilePairs] = useState([]);
  const [currentPairIndex, setCurrentPairIndex] = useState(0);
  const [previewInFile, setPreviewInFile] = useState("");
  const [previewOutFile, setPreviewOutFile] = useState("");
  const [zoomLevel, setZoomLevel] = useState(1);
  // Archived preview: XML + boxes
  const [xmlInUrl, setXmlInUrl] = useState("NA");
  const [xmlOutUrl, setXmlOutUrl] = useState("NA");
  const [inBoxes, setInBoxes] = useState([]);
  const [outBoxes, setOutBoxes] = useState([]);
  const [inNaturalSize, setInNaturalSize] = useState({ width: 1, height: 1 });
  const archiveFetchSeq = useRef(0);

  const [inputKey, setInputKey] = useState(0);
  const isMountedRef = useRef(true);
  const abortControllerRef = useRef(null);
  const builtinUseCases = ["Guns", "WBI", "Orientation"];

  // Date filters default: yesterday -> today
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  const [startDate, setStartDate] = useState(yesterday);
  const [endDate, setEndDate] = useState(today);
  const [archiveSearched, setArchiveSearched] = useState(false);
  const [archiveLoading, setArchiveLoading] = useState(false);

  // Instance
  const instanceId = "i-0e5494d7c4d042b3d"; // TC-Inf-EC2

  // ===== Derived flags & labels (fixed) =====
  const ec2StateLower = String(ec2Status || "").toLowerCase();
  const scriptStateLower = String(infScriptStatus || "").toLowerCase();
  const ec2IsRunning = ec2StateLower === "running";
  const ec2IsStopped = ec2StateLower === "stopped";
  const ec2IsStopping = /stopping|shutting/i.test(ec2StateLower) || stoppingEC2;
  const showStopScriptButton = ec2IsRunning && scriptStateLower === "running";
  const stopScriptDisabled   = !ec2IsRunning || startingScript || stoppingScript;

  // If EC2 isn't running (stopped/stopping), script should appear stopped.
  // If user clicked stop, show "stopping".
  const scriptLabel = !ec2IsRunning || ec2IsStopping
    ? (stoppingEC2 ? "stopping" : "stopped")
    : (infScriptStatus || "starting");

  const isStartingOrStopping =
    /starting|stopping/i.test(scriptLabel) || /please wait/i.test(scriptLabel);

  const scriptColor =
    /running/i.test(scriptLabel) ? "green" :
    /stopped/i.test(scriptLabel) ? "gray" :
    /stopping/i.test(scriptLabel) ? "orange" : "orange";

  const hasUseCase = !!selectedUseCase;
  const accepted = !!checkboxAccepted;
  const canStartVm = ec2IsStopped && hasUseCase && accepted && !startingEC2 && !stoppingEC2;
  const canStopVm = ec2IsRunning && !startingEC2 && !stoppingEC2;

  const showStartScriptButton =
    ec2Status === "running" &&
    scriptStateLower === "stopped" 
    // && user?.organization?.toLowerCase() === "techcomb"
    ;

  // New state for navigation & overlays (upload)
  const [currentIndex, setCurrentIndex] = useState(0);
  const uploadedImageFiles = uploadedFiles?.filter((f) => /\.(jpe?g|png|bmp|gif|webp)$/i.test(f)) || [];
  const totalUploadImages = uploadedImageFiles.length;
  const currentDisplayIndex = Math.min(currentIndex + 1, totalUploadImages || 1);

  // Current file for preview
  const currentImageName = uploadedImageFiles[currentIndex];
  const inferredImageName = `InferredImg_${organization}_${currentImageName}`;
  const xmlFileName = currentImageName?.replace(/\.[^.]+$/, ".xml");
  const xmlUrl = inferenceResults[xmlFileName];

  // Upload preview: date prefix (YYYY/MM/DD)
  const [uploadDatePrefix, setUploadDatePrefix] = useState("");

  // Upload preview: URLs + boxes
  const [gtImageUrl, setGtImageUrl] = useState("");
  const [gtXmlUrl, setGtXmlUrl] = useState("NA");
  const [gtBoxes, setGtBoxes] = useState([]);
  const [gtNaturalSize, setGtNaturalSize] = useState({ width: 1, height: 1 });
  const [infImageUrl, setInfImageUrl] = useState("");
  const seen = useRef(new Set());
  const [cursor, setCursor] = useState(null);
  const [streamPairs, setStreamPairs] = useState([]);
  const streamSeen = useRef(new Set());
  const [streamCursor, setStreamCursor] = useState(null);


  const parseXmlToBoxes = (xmlText) => {
    try {
      const xml = new DOMParser().parseFromString(xmlText, "application/xml");
      if (xml.getElementsByTagName("parsererror").length) return [];
      const boxes = [];
      const objects = Array.from(xml.getElementsByTagName("object"));
      if (objects.length > 0) {
        for (const obj of objects) {
          const bnd = obj.getElementsByTagName("bndbox")[0];
          if (!bnd) continue;
          const xmin = Number(bnd.getElementsByTagName("xmin")[0]?.textContent ?? NaN);
          const ymin = Number(bnd.getElementsByTagName("ymin")[0]?.textContent ?? NaN);
          const xmax = Number(bnd.getElementsByTagName("xmax")[0]?.textContent ?? NaN);
          const ymax = Number(bnd.getElementsByTagName("ymax")[0]?.textContent ?? NaN);
          if ([xmin, ymin, xmax, ymax].every((n) => Number.isFinite(n))) {
            boxes.push({ xmin, ymin, xmax, ymax });
          }
        }
      } else {
        const xmin = Number(xml.getElementsByTagName("xmin")[0]?.textContent ?? NaN);
        const ymin = Number(xml.getElementsByTagName("ymin")[0]?.textContent ?? NaN);
        const xmax = Number(xml.getElementsByTagName("xmax")[0]?.textContent ?? NaN);
        const ymax = Number(xml.getElementsByTagName("ymax")[0]?.textContent ?? NaN);
        if ([xmin, ymin, xmax, ymax].every((n) => Number.isFinite(n))) {
          boxes.push({ xmin, ymin, xmax, ymax });
        }
      }
      return boxes;
    } catch {
      return [];
    }
  };

  // put these above loadUserModels (inside the component)
  const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

  const zipBaseName = (key) => {
    if (!key) return null;
    const s = String(key).trim();
    const file = s.split("/").pop() || "";
    if (!/\.zip$/i.test(file)) return null;
    return file.replace(/\.zip$/i, ""); // => sectest_1758664103751_3K
  };

  // (optional) sort by base name then by step number (2K, 3K, 5K...)
  const sortByBaseThenStep = (a, b) => {
    const pa = a.match(/^(.*)_([0-9]+)K$/i);
    const pb = b.match(/^(.*)_([0-9]+)K$/i);
    const baseA = pa ? pa[1] : a, baseB = pb ? pb[1] : b;
    const cmpBase = collator.compare(baseA, baseB);
    if (cmpBase !== 0) return cmpBase;
    const stepA = pa ? parseInt(pa[2], 10) : Number.MAX_SAFE_INTEGER;
    const stepB = pb ? parseInt(pb[2], 10) : Number.MAX_SAFE_INTEGER;
    return stepA - stepB;
  };

  const disableUseCaseSelect =
  scriptStateLower === "running" || isStartingOrStopping || startingScript || stoppingScript;

  const loadUserModels = useCallback(async () => {
    setLoadingModels(true);
    setModelsError(null);
    try {
      const { files } = await listModelsAPI(organization);

      const names = (files || [])
        .filter(Boolean)
        .filter((v, i, arr) => arr.indexOf(v) === i)
        .sort(sortByBaseThenStep);

      setUserTrainedModels(names);
    } catch (err) {
      console.error("Failed to load user-trained models:", err);
      setModelsError("Could not load models from database");
      setUserTrainedModels([]);
    } finally {
      setLoadingModels(false);
    }
  }, [organization]);


  useEffect(() => {
    // Only run if we have files and a date prefix from the latest upload
    if (!uploadDatePrefix || !currentImageName) {
      setGtImageUrl("");
      setInfImageUrl("");
      setGtXmlUrl("NA");
      setGtBoxes([]);
      return;
    }

    const run = async () => {
      console.log(currentImageName);
      try {
        // Keys
        const inImgKey = `in/${uploadDatePrefix}/${currentImageName}`;
        const outImgKey = `out/${uploadDatePrefix}/InferredImg_${organization}_${currentImageName}`;
        const inXmlKey  = `in/${uploadDatePrefix}/${currentImageName.replace(/\.[^.]+$/, ".xml")}`;
        const outXmlKey = `out/${uploadDatePrefix}/InferredImg_${organization}_${currentImageName.replace(/\.[^.]+$/, ".xml")}`;

        // Fetch presigned URLs
        const [inImgResp, outImgResp] = await Promise.all([
          getPresignedUrlAPI(organization, inImgKey).catch(() => ({ url: "" })),
          getPresignedUrlAPI(organization, outImgKey).catch(() => ({ url: "" })),
        ]);

        setGtImageUrl(inImgResp?.url || "");
        setInfImageUrl(outImgResp?.url || "");

        // Try input XML first, then fallback to output XML
        let xmlUrlLocal = "NA";
        try {
          const inXmlResp = await getPresignedUrlAPI(organization, inXmlKey);
          xmlUrlLocal = inXmlResp?.url || "NA";
        } catch {
          try {
            const outXmlResp = await getPresignedUrlAPI(organization, outXmlKey);
            xmlUrlLocal = outXmlResp?.url || "NA";
          } catch {
            xmlUrlLocal = "NA";
          }
        }

        setGtXmlUrl(xmlUrlLocal);

        // Parse boxes if we got an http URL
        if (xmlUrlLocal && xmlUrlLocal.startsWith("http")) {
          const txt = await (await fetch(xmlUrlLocal)).text();
          setGtBoxes(parseXmlToBoxes(txt));
        } else {
          setGtBoxes([]);
        }
      } catch (e) {
        console.error("Upload preview fetch error:", e);
        setGtImageUrl("");
        setInfImageUrl("");
        setGtXmlUrl("NA");
        setGtBoxes([]);
      }
    };

    run();
  }, [currentIndex, currentImageName, organization, uploadDatePrefix]);

  useEffect(() => {
    loadUserModels();
  }, [loadUserModels]);

  // Initial statuses
  useEffect(() => {
    checkEC2Status();
  }, []); // eslint-disable-line

  useEffect(() => () => { isMountedRef.current = false; }, []);

  // Update archived preview URLs when navigating within results
  useEffect(() => {
    fetchPreviewUrls();
    setZoomLevel(1);
  }, [currentPairIndex, filePairs]); // eslint-disable-line

  // Keep uploaded preview in sync with watcher
  useEffect(() => {
    if (selectedFile && inferenceResults[selectedFile]?.startsWith("http")) {
      setUploadedPreviewUrl(inferenceResults[selectedFile]);
    }
  }, [inferenceResults, selectedFile]);


  useEffect(() => {
    if (!isStreaming || !streamingRunning) {
      // reset when streaming is off
      setStreamPairs([]);
      streamSeen.current.clear();
      setStreamCursor(null);
      return;
    }

    let cancel = false;
    let timer = null;

    // Keep the paging cursor locally so the closure isn't stale
    let cursorLocal = null;

    const fmtUtcDate = (d) =>
      `${d.getUTCFullYear()}/${String(d.getUTCMonth() + 1).padStart(2, "0")}/${String(d.getUTCDate()).padStart(2, "0")}`;

    const unwrap = (resp) => (resp?.message ? resp.message : resp) || {};

    async function fetchPage(dateFolder, cursor) {
      const resp = await listReadyPairsAPI(organization, dateFolder, 200, cursor);
      const payload = unwrap(resp);
      return {
        items: Array.isArray(payload.items) ? payload.items : [],
        next: payload.nextCursor ?? null,
      };
    }

    async function tick() {
      try {
        const now = new Date();
        const today = fmtUtcDate(now);

        // 1) try "today (UTC)"
        let { items, next } = await fetchPage(today, cursorLocal);

        // 2) if empty, try "yesterday (UTC)" from the beginning
        if (!items.length) {
          const y = new Date(now);
          y.setUTCDate(y.getUTCDate() - 1);
          const yDate = fmtUtcDate(y);
          ({ items, next } = await fetchPage(yDate, null));
          // switch cursorLocal to yesterday’s chain if we found data there
          if (items.length) cursorLocal = next;
        } else {
          cursorLocal = next;
        }

        // Deduplicate by baseName and keep newest-first; cap list length
        if (items.length) {
          const fresh = [];
          for (const it of items) {
            if (!streamSeen.current.has(it.baseName)) {
              streamSeen.current.add(it.baseName);
              fresh.push(it); // { baseName, inFile, outFile }
            }
          }
          if (fresh.length) {
            setStreamPairs((prev) => [...fresh, ...prev].slice(0, 500));
          }
        }

        // Expose cursor for UI/debug; polling uses cursorLocal internally
        setStreamCursor(cursorLocal);
      } catch (e) {
        console.error("stream poll error:", e);
      } finally {
        if (!cancel) timer = setTimeout(tick, 2500);
      }
    }

    // Reset state when (re)starting streaming mode
    setStreamPairs([]);
    streamSeen.current.clear();
    setStreamCursor(null);
    cursorLocal = null;

    tick();
    return () => {
      cancel = true;
      if (timer) clearTimeout(timer);
    };
  }, [isStreaming, streamingRunning, organization]);



  // --- Helpers ---
  const extractMessage = (resp) => {
    try {
      if (typeof resp === "string") {
        const p = JSON.parse(resp);
        return p?.message ?? p?.body?.message ?? undefined;
      }
      if (resp && typeof resp === "object") {
        if (resp.body != null) {
          const payload = typeof resp.body === "string" ? JSON.parse(resp.body) : resp.body;
          return payload?.message;
        }
        return resp.message;
      }
    } catch (_) {}
    return undefined;
  };

  const pollScriptStatus = useCallback(
    async ({ timeoutMs = 60000, intervalMs = 3000 } = {}) => {
      const started = Date.now();
      while (Date.now() - started < timeoutMs) {
        try {
          const statusResp = await checkEC2StatusAPI(instanceId, true);
          const script = String(statusResp?.script || "").toLowerCase();
          if (!isMountedRef.current) return;
          setInfScriptStatus(script || "starting");
          if (script === "running" || script === "stopped" || script === "error") return;
        } catch {
          // ignore transient errors
        }
        await new Promise((r) => setTimeout(r, intervalMs));
      }
    },
    [instanceId]
  );

  const checkEC2Status = useCallback(async () => {
    try {
      const response = await checkEC2StatusAPI(instanceId, true);
      setEc2Status(response.ec2 || "unknown");
      setInfScriptStatus(response.script ?? "stopped");
    } catch (error) {
      setEc2Status("error");
      setInfScriptStatus("stopped");
    }
  }, [instanceId]);

  // persist use case
  useEffect(() => {
    const saved = localStorage.getItem("selectedUseCase");
    if (saved) setSelectedUseCase(saved);
  }, []);

  useEffect(() => {
    if (selectedUseCase) localStorage.setItem("selectedUseCase", selectedUseCase);
  }, [selectedUseCase]);
  

  const fetchPreviewUrls = useCallback(async () => {
    const seq = ++archiveFetchSeq.current;

    const currentPair = filePairs[currentPairIndex];
    if (!currentPair) {
      setPreviewInFile("");
      setPreviewOutFile("");
      setXmlInUrl?.("NA");
      setXmlOutUrl?.("NA");
      setInBoxes?.([]);
      setOutBoxes?.([]);
      return;
    }

    const { inFile, outFile } = currentPair;
    setPreviewInFile("");
    setPreviewOutFile("");

    try {
      const { inFile, outFile, inXml, outXml } = currentPair;
      const xmlInKey  = inXml  || (inFile  ? inFile.replace(/\.[^.]+$/, ".xml")  : null);
      const xmlOutKey = outXml || (outFile ? outFile.replace(/\.[^.]+$/, ".xml") : null);

      const [inUrlResponse, outUrlResponse, xmlInResp, xmlOutResp] = await Promise.all([
        inFile ? getPresignedUrlAPI(organization, inFile) : Promise.resolve({ url: "" }),
        outFile ? getPresignedUrlAPI(organization, outFile) : Promise.resolve({ url: "" }),
        xmlInKey
          ? getPresignedUrlAPI(organization, xmlInKey).catch(() => ({ url: "NA" }))
          : Promise.resolve({ url: "NA" }),
        xmlOutKey
          ? getPresignedUrlAPI(organization, xmlOutKey).catch(() => ({ url: "NA" }))
          : Promise.resolve({ url: "NA" }),
      ]);
      
      console.log(inUrlResponse, outUrlResponse, xmlInResp, xmlOutResp);

      if (seq !== archiveFetchSeq.current) return;

      setPreviewInFile(inUrlResponse?.url || "");
      setPreviewOutFile(outUrlResponse?.url || "");
      setXmlInUrl?.(xmlInResp?.url || "NA");
      setXmlOutUrl?.(xmlOutResp?.url || "NA");

      if (xmlInResp?.url && xmlInResp.url.startsWith("http")) {
        const txt = await (await fetch(xmlInResp.url)).text();
        setInBoxes?.(parseXmlToBoxes(txt));
      } else {
        setInBoxes?.([]);
      }

      if (xmlOutResp?.url && xmlOutResp.url.startsWith("http")) {
        const txtOut = await (await fetch(xmlOutResp.url)).text();
        setOutBoxes?.(parseXmlToBoxes(txtOut));
      } else {
        setOutBoxes?.([]);
      }
    } catch (err) {
      console.error("Failed to load preview URLs:", err);
      if (seq !== archiveFetchSeq.current) return;
      setPreviewInFile("");
      setPreviewOutFile("");
      setXmlInUrl?.("NA");
      setXmlOutUrl?.("NA");
      setInBoxes?.([]);
      setOutBoxes?.([]);
    }
  }, [filePairs, currentPairIndex, organization]);

  const fetchFiles = useCallback(
    async (sDate, eDate) => {
      // Clear archived preview state immediately to avoid showing stale images
      setFilePairs([]);
      setCurrentPairIndex(0);
      setPreviewInFile("");
      setPreviewOutFile("");
      setZoomLevel(1);

      setXmlInUrl?.("NA");
      setXmlOutUrl?.("NA");
      setInBoxes?.([]);
      setOutBoxes?.([]);
      setInNaturalSize?.({ width: 1, height: 1 });

      try {
        const start = sDate ? sDate.toISOString().split("T")[0] : null;
        const end = eDate ? eDate.toISOString().split("T")[0] : null;

        const response = await fetchFilesAPI(organization, start, end);

        const previewPairs = (response.files || []).map((file) => ({
          baseName: file.baseName,
          inFile: file.inFile,
          outFile: file.outFile,
        }));

        setFilePairs(previewPairs);
        setCurrentPairIndex(0);
      } catch (error) {
        console.error("Error fetching archive file pairs:", error);

        // Ensure UI stays clean on error
        setFilePairs([]);
        setCurrentPairIndex(0);
        setPreviewInFile("");
        setPreviewOutFile("");
        setZoomLevel(1);
        setXmlInUrl?.("NA");
        setXmlOutUrl?.("NA");
        setInBoxes?.([]);
        setOutBoxes?.([]);
        setInNaturalSize?.({ width: 1, height: 1 });
      }
    },
    [organization]
  );

  const handleArchiveSearch = async () => {
    if (!startDate || !endDate) {
      alert("Please select a start and end date.");
      return;
    }
    setArchiveSearched(true);
    setArchiveLoading(true);
    const resp = await fetchFiles(startDate, endDate);
    console.log(resp);
    setArchiveLoading(false);
  };

  const generatePresignedUrls = async ({
    files,
    action,
    datasetName,
    useFullRelativePath = false,
  }) => {
    const payload = {
      action: "getPresignedUrl",
      organization,
      datasetName,
      username,
      operation: action,
      files: files.map((f) => {
        const relativePath = typeof f === "string" ? f : f.webkitRelativePath || f.name;
        const cleanedPath = relativePath.split("/").slice(1).join("/");
        return {
          name: useFullRelativePath ? `${cleanedPath}` : `${relativePath}`,
          type: f.type || "application/octet-stream",
        };
      }),
    };
    const { data } = await apiClient.post("modeltesting", payload);
    return data;
  };

  // --- Actions ---
  const handleStartEC2Click = () => {
    if (showCheckbox && !checkboxAccepted) {
      alert("Please accept the user information about costs before starting the VM.");
      return;
    }
    if (!selectedUseCase) {
      alert("Please select a use case to start the VM.");
      return;
    }
    setAcceptedConfirmed(true);
    handleStartEC2();
  };

  const handleStopEC2Click = () => {
    handleStopEC2();
    setAcceptedConfirmed(false);
    setCheckboxAccepted(false);
    setShowCheckbox(true);
  };

  const handleStartEC2 = async () => {
    if (!selectedUseCase) {
      alert("Please select a use case to start the VM.");
      return;
    }
    if (!checkboxAccepted) {
      alert("Please accept and agree to the checkbox for VM charges.");
      return;
    }

    setShowCheckbox(false);
    setStartingEC2(true);
    setEc2Status("Please wait. Starting.....");

    try {
      // Start EC2 only
      const startResp = await startEC2API(instanceId, selectedUseCase);

      // Expect backend to return { message: "running", script: "stopped" }
      const ec2Msg = extractMessage(startResp) || "unknown";
      setEc2Status(ec2Msg);

      // Refresh status (includes script when includeScript=true on the server)
      const statusResp = await checkEC2StatusAPI(instanceId, true);
      const script = String(statusResp?.script || "stopped").toLowerCase();
      setInfScriptStatus(script);
    } catch (error) {
      console.error("Error starting EC2:", error);
      setEc2Status("error");
      setInfScriptStatus("stopped");
    } finally {
      setStartingEC2(false);
    }
  };


  const handleRefreshStatuses = async () => {
    await checkEC2Status();
  };

  // ===== fixed: show "stopping" immediately; coerce script to stopped when EC2 goes down
  const handleStopEC2 = async () => {
    setStoppingEC2(true);
    setInfScriptStatus("stopping"); // optimistic UI during transition
    setEc2Status("stopping");
    try {
      await stopEC2API(instanceId);
    } catch (error) {
      console.error("Error stopping EC2:", error);
    } finally {
      setStoppingEC2(false);
    }
    await checkEC2Status(); // refresh ec2+script; if not running, scriptLabel shows 'stopped'
    setInfScriptStatus(""); // let derive logic use EC2 state
  };

  const handleStartScript = async () => {
    if (!ec2IsRunning) {
      alert("Please start the EC2 before starting the script.");
      return;
    }
    try {
      setStartingScript(true);
      setInfScriptStatus("starting");

      await startDetectionScriptAPI(instanceId, selectedUseCase);
      await pollScriptStatus({ timeoutMs: 60000, intervalMs: 3000 });
      await checkEC2Status();
    } catch (err) {
      console.error("Error starting script:", err);
      setInfScriptStatus("error");
    } finally {
      setStartingScript(false);
    }
  };

  const handleUploadMultiple = async () => {
    if (!selectedFiles || selectedFiles.length === 0) {
      alert("Please select files to upload.");
      return;
    }
    if (!ec2IsRunning || scriptStateLower !== "running") {
      alert("Uploads are enabled only after both EC2 and the inference script are running.");
      return;
    }

    // ----- Clear previous UI -----
    setInferenceStatusMessage("");
    setUploadedFiles([]);
    setSelectedFile(null);
    setUploadedPreviewUrl("");
    setInferenceResults({});
    setShowFileList(false);

    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;

    setIsUploading(true);
    setUploadProgress(0);
    setUploadedCount(0);

    const successfulUploads = [];
    const failedUploads = [];

    // Build the date prefix and keep it for per-image preview fetching later
    const now = new Date();
    const datePrefix = `${now.getUTCFullYear()}/${String(now.getUTCMonth() + 1).padStart(2, "0")}/${String(
      now.getUTCDate()
    ).padStart(2, "0")}`;
    const datasetFolderName = `inference/in/${datePrefix}`;
    const s3OutPrefix = `inference/out/${datePrefix}`;

    setUploadDatePrefix(datePrefix);

    const isImage = (name) => /\.(jpe?g|png|bmp|gif|webp)$/i.test(name);

    const batches = buildDynamicFileBatches(selectedFiles, 300000);
    const limit = pLimit(10);
    const totalFileCount = selectedFiles.length;
    let completedCount = 0;

    console.time("Upload Time");

    try {
      for (const batch of batches) {
        if (signal.aborted) throw new Error("Upload canceled by user.");

        const urls = await generatePresignedUrls({
          files: batch,
          action: "upload",
          datasetName: datasetFolderName,
          useFullRelativePath: false,
        });

        const uploadTasks = urls.map((urlObj) =>
          limit(async () => {
            const file = batch.find((f) => f.name === urlObj.filename);
            try {
              if (!file) throw new Error("File not found in batch.");
              await apiClient.put(urlObj.url, file, {
                headers: { "Content-Type": urlObj.contentType || "application/octet-stream" },
                signal,
              });
              successfulUploads.push(file.name);
            } catch (err) {
              console.error("Upload failed:", file?.name, err);
              failedUploads.push(file?.name || "unknown");
            } finally {
              completedCount++;
              setUploadedCount(completedCount);
              setUploadProgress(Math.floor((completedCount / totalFileCount) * 100));
            }
          })
        );

        await Promise.all(uploadTasks);

        if (successfulUploads.length > 0) {
          setShowFileList(true);
        }
      }

      alert(`Upload Complete!\nSuccess: ${successfulUploads.length}\nFailed: ${failedUploads.length}`);

      // Keep full list for UI; upload preview filters to images
      setUploadedFiles(successfulUploads);

      // ---- Only IMAGES go to EC2 for inference ----
      const imageOnlyUploads = successfulUploads.filter(isImage);

      if (imageOnlyUploads.length === 0) {
        setInferenceStatusMessage("No image files were uploaded to run inference.");
        setWaitingForInference(false);
        setIsUploading(false);
        setUploadProgress(0);
        return;
      }

      // Trigger EC2 to download only the image files
      await handleDownloadForInference(organization, datasetFolderName, imageOnlyUploads);

      // Watcher: expect outputs only for image files
      setWaitingForInference(true);
      setInferenceStatusMessage("Getting Inference results.....");

      await triggerInferenceFileWatcherAPI(organization, s3OutPrefix, imageOnlyUploads)
        .then((result) => {
          setInferenceResults(result.message || {});
          setInferenceStatusMessage(
            "Inference results are ready. Use navigation to preview each file."
          );
          setWaitingForInference(false);
        })
        .catch((err) => {
          console.error("Watcher failed:", err);
          setInferenceStatusMessage("Inference output is not available. Please check with the admin.");
          setWaitingForInference(false);
        });

      // Finish progress UI
      setUploadProgress(100);
      setTimeout(() => {
        setUploadProgress(0);
        setIsUploading(false);
      }, 600);

      // Reset file input
      setInputKey((k) => k + 1);
      setCurrentIndex(0);
    } catch (error) {
      if (error?.message === "Upload canceled by user.") {
        alert("Upload was canceled.");
      } else {
        console.error("Error during upload:", error);
        alert("An error occurred during upload. Check console for details.");
      }
    } finally {
      console.timeEnd("Upload Time");
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDownloadForInference = async (organization, datePrefix, fileList) => {
    try {
      await triggerDownloadForInferenceToEC2(instanceId, organization, datePrefix, fileList);
      alert("Download completed successfully!");
    } catch (err) {
      alert("Failed to trigger download.");
    }
  };

  const handleCancelUpload = async () => {
    // Implement if you wire cancellation into apiClient with AbortController
  };

  const handleStopScript = async () => {
    if (!ec2IsRunning) return;
    try {
      setStoppingScript(true);
      setInfScriptStatus("stopping"); // optimistic UI
      await stopDetectionScriptAPI(instanceId);
      // short poll to reflect the new state
      await pollScriptStatus({ timeoutMs: 20000, intervalMs: 2000 });
      await checkEC2Status();
    } catch (err) {
      console.error("Error stopping script:", err);
      setInfScriptStatus("error");
    } finally {
      setStoppingScript(false);
    }
  };


  const handleStreamingToggle = () => {
    if (streamingRunning) {
      stopStreaming();
    } else {
      startStreaming();
    }
  };


  const startStreaming = async () => {
    if (!ec2IsRunning) {
      alert("Please start the EC2 before starting streaming inference.");
      return;
    }
    if (!selectedUseCase) {
      alert("Please select a use case.");
      return;
    }
    const streamName = streamingBucket.trim();
    if (!streamName) {
      alert("Please enter a Stream Name.");
      return;
    }

    try {
      setStartingStreaming(true);
      // Fire-and-forget orchestrator: starts PRODUCER, waits ready, then CONSUMER.
      const resp = await orchestrateKVSStackAPI(instanceId, "start", {
        streamName: streamingBucket.trim(),
        organization,
        region: "us-east-1",
      });
      const msg = String(resp?.message || "");
      if (/dispatched/i.test(msg)) {
        // Optimistic UI: show “starting …”; user can watch logs or we can add a small poll later.
        setStreamingRunning(true);
        setInfScriptStatus("running");
      } else {
        alert(`Could not start streaming: ${msg || "Unknown error"}`);
      }
    } catch (e) {
      console.error(e);
      alert("Failed to start streaming.");
    } finally {
      setStartingStreaming(false);
    }
  };
 

  const stopStreaming = async () => {
    try {
      setStoppingStreaming(true);
      const resp = await orchestrateKVSStackAPI(instanceId, "stop", {
        streamName: streamingBucket.trim(),
        organization,
      });
      const msg = String(resp?.message || "");
      if (/dispatched|stopped|success/i.test(msg)) {
        setStreamingRunning(false);
      } else {
        alert(`Could not stop streaming: ${msg || "Unknown error"}`);
      }
    } catch (e) {
      console.error(e);
      alert("Failed to stop streaming.");
    } finally {
      setStoppingStreaming(false);
    }
  };

  
  return (
    <Box>
      {/* EC2 Instance Control */}
      <div align="center" style={{ paddingTop: "0px" }}>
        <Row className="justify-content-center" style={{ margin: "0 auto", maxWidth: "1200px" }}>
          <Col sm="12" md="12">
            <ComponentCard>
              <Typography variant="body1" sx={{ fontWeight: "bold", marginBottom: "10px" }}>
                EC2 Instance Control
              </Typography>

              {ec2Status !== "running" ? (
                <Typography
                  variant="body1"
                  sx={{
                    color: "red",
                    fontSize: "12px",
                    textAlign: "center",
                    maxWidth: "600px",
                    marginTop: "15px",
                  }}
                >
                  Clicking the <strong>Start VM</strong> button will start the GPU-enabled virtual
                  machine to run inferencing using a trained model. In running state,{" "}
                  <span style={{ textDecoration: "underline", fontSize: "14px" }}>
                    <strong>the machine will incur costs every hour.</strong>
                  </span>
                </Typography>
              ) : (
                <Typography
                  variant="body1"
                  sx={{
                    color: "red",
                    fontSize: "13px",
                    textAlign: "center",
                    maxWidth: "600px",
                    marginTop: "15px",
                  }}
                >
                  Please click on the <strong>Stop VM</strong> button to turn the machine off when
                  not in use.
                </Typography>
              )}

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "20px",
                  marginTop: "10px",
                }}
              >
                {ec2Status === "stopped" && !acceptedConfirmed && showCheckbox && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={checkboxAccepted}
                        onChange={(e) => setCheckboxAccepted(e.target.checked)}
                        size="small"
                      />
                    }
                    label={<Typography sx={{ fontSize: "14px" }}>I accept</Typography>}
                  />
                )}
              </div>

              {ec2Status !== "stopped" && (
                <Typography
                  variant="body2"
                  sx={{
                    color: "black",
                    fontSize: "14px",
                    textAlign: "center",
                    maxWidth: "600px",
                    marginTop: "15px",
                  }}
                >
                  Use refresh button to view the states of the machine and the script.
                </Typography>
              )}

              <TableContainer component={Paper} sx={{ maxWidth: 800, margin: "auto", marginTop: "20px" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell align="center">
                        <strong>Model Use Case</strong>
                      </TableCell>
                      <TableCell align="center">
                        <strong>EC2 Status</strong>
                      </TableCell>
                      <TableCell align="center">
                        <strong>Inference Environment Status</strong>
                      </TableCell>
                      <TableCell align="center">
                        <strong>Action</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell align="center">
                        <select
                          value={selectedUseCase}
                          disabled={disableUseCaseSelect}
                          title={disableUseCaseSelect ? "Stop the script to change the use case" : ""}
                          onChange={(e) => setSelectedUseCase(e.target.value)}
                          style={{
                            padding: "6px",
                            fontSize: "14px",
                            minWidth: 220,
                            border: "1px solid rgba(0,0,0,0.23)",
                            borderRadius: 4,
                            outline: "none",
                            boxSizing: "border-box",
                            backgroundColor: disableUseCaseSelect ? "#f5f5f5" : "white",
                            cursor: disableUseCaseSelect ? "not-allowed" : "pointer",
                            opacity: disableUseCaseSelect ? 0.8 : 1,
                          }}
                        >
                          <option value="">-- Select a use case --</option>

                          <optgroup label="Prebuilt Models">
                            {builtinUseCases.map((u) => (
                              <option key={`builtin-${u}`} value={u}>
                                {u}
                              </option>
                            ))}
                          </optgroup>
                          <optgroup label="Your Trained Models">
                            {loadingModels && <option disabled>Loading…</option>}

                            {!loadingModels && modelsError && (
                              <option disabled>⚠ {modelsError}</option>
                            )}

                            {!loadingModels && !modelsError && userTrainedModels.length === 0 && (
                              <option disabled>(none found)</option>
                            )}

                            {!loadingModels &&
                              !modelsError &&
                              userTrainedModels.map((m) => (
                                <option key={`user-${m}`} value={m}>
                                  {m}
                                </option>
                              ))}
                          </optgroup>
                        </select>

                      </TableCell>

                      <TableCell align="center">
                        <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                          <Tooltip title={ec2IsRunning ? "Stop VM" : "Start VM"}>
                            <span>
                              <IconButton
                                onClick={ec2IsRunning ? handleStopEC2Click : handleStartEC2Click}
                                disabled={ec2IsRunning ? !canStopVm : !canStartVm}
                                color={!ec2IsRunning ? "primary" : "error"}
                              >
                                {ec2IsRunning ? <StopIcon /> : <PlayArrowIcon />}
                              </IconButton>
                            </span>
                          </Tooltip>

                          <Typography
                            variant="body2"
                            sx={{
                              color:
                                ec2Status === "running"
                                  ? "green"
                                  : ec2Status === "stopped"
                                  ? "gray"
                                  : /starting/i.test(ec2Status) || /stopping/i.test(ec2Status)
                                  ? "orange"
                                  : "red",
                              fontWeight: "bold",
                              textTransform: "capitalize",
                            }}
                          >
                            {ec2Status || "Loading..."}
                          </Typography>

                          {(/starting/i.test(ec2Status) || /stopping/i.test(ec2Status)) && (
                            <CircularProgress size={16} color="warning" />
                          )}
                        </Box>
                      </TableCell>

                      <TableCell align="center">
                        <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                          {showStartScriptButton && (
                            <Tooltip title="Start Detection Script">
                              <span>
                                <IconButton color="primary" onClick={handleStartScript}>
                                  <PlayArrowIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}

                          {showStopScriptButton && (
                            <Tooltip title="Stop Detection Script">
                              <span>
                                <IconButton color="error" onClick={handleStopScript} disabled={stopScriptDisabled}>
                                  <StopIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}

                          <Typography variant="body2" sx={{ color: scriptColor, fontWeight: "bold", textTransform: "capitalize" }}>
                            {scriptLabel}
                          </Typography>

                          {(startingScript || isStartingOrStopping) && (
                            <CircularProgress size={16} color="warning" />
                          )}
                        </Box>
                      </TableCell>

                      <TableCell align="center">
                        <Tooltip title="Check VM/Script Status">
                          <IconButton onClick={handleRefreshStatuses} color="default">
                            <RefreshIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </ComponentCard>
          </Col>
        </Row>
      </div>

      {/* Run Inference Section */}
      <div align="center" style={{ paddingTop: "0px" }}>
        <Row className="justify-content-center" style={{ margin: "0 auto", maxWidth: "1200px" }}>
          <Col sm="12" md="12">
            <ComponentCard>
              {/* Header: title centered, switch on the right */}
              <Box
                sx={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: 40,
                  mb: 1,
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                  Run Inference
                </Typography>

                <Box
                  sx={{
                    position: "absolute",
                    right: 16,
                    top: "50%",
                    transform: "translateY(-50%)",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ opacity: isStreaming ? 0.5 : 1 }}>
                    Batch
                  </Typography>
                  <Switch
                    size="small"
                    checked={isStreaming}
                    onChange={(e) => setInferenceMode(e.target.checked ? "streaming" : "batch")}
                    color="primary"
                    inputProps={{ "aria-label": "Toggle inference mode" }}
                  />
                  <Typography variant="body2" sx={{ opacity: isStreaming ? 1 : 0.5 }}>
                    Streaming
                  </Typography>
                </Box>
              </Box>

              {/* Content: conditional on mode */}
              {isStreaming ? (
                <>
                <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, width: "100%", maxWidth: 600 }}>
                    <Typography
                      component="label"
                      htmlFor="streaming-bucket"
                      variant="body2"
                      sx={{ minWidth: 130, lineHeight: "40px", fontWeight: "bold" }}
                    >
                      Stream Name
                    </Typography>

                    <TextField
                      id="streaming-bucket"
                      size="small"
                      fullWidth
                      placeholder="e.g. my-video-bucket"
                      value={streamingBucket}
                      onChange={(e) => setStreamingBucket(e.target.value)}
                      sx={{
                        "& .MuiInputBase-root": { height: 38 },
                        "& .MuiInputBase-input": { padding: "8px 12px" },
                      }}
                    />

                    <Button
                      color="outline-secondary"
                      onClick={handleStreamingToggle}
                      disabled={
                        startingStreaming ||
                        stoppingStreaming ||
                        !ec2IsRunning ||
                        !selectedUseCase ||
                        !streamingBucket.trim()
                      }
                      style={{
                        backgroundColor: "#eaf9ea",
                        color: "grey",
                        fontSize: "15px",
                        marginLeft: "10px",
                        marginRight: "10px",
                        minWidth: "140px",
                        height: "38px",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "0 16px",
                      }}
                    >
                      {streamingRunning
                        ? (stoppingStreaming ? "Stopping..." : "Stop Inference")
                        : (startingStreaming ? "Starting..." : "Start Inference")}
                    </Button>
                  </Box>
                </Box>
                <Box sx={{ mt: 3 }}>
                  {streamPairs.length === 0 ? (
                    <Typography variant="body2" sx={{ color: "gray", textAlign: "center" }}>
                      {streamingRunning ? "Waiting for frames…" : "Start Inference to see streaming results."}
                    </Typography>
                  ) : (
                    <LoadImagePreview
                      mode="archive"
                      organization={organization}
                      filePairs={streamPairs}   
                      getPresignedUrl={getPresignedUrlAPI} 
                      containerWidth={400}
                      containerHeight={300}
                      showZoomControls={true}
                    />
                  )}
                </Box>
              </>
              ) : (
                <>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "red",
                      fontSize: "12px",
                      textAlign: "center",
                      mb: "15px",
                      maxWidth: "600px",
                      m: "0 auto",
                    }}
                  >
                    Uploads are enabled only after both EC2 and Inference Script are in Running status. <br />
                    <span style={{ fontSize: "14px" }}>
                      <strong>Start VM</strong>
                    </span>{" "}
                    in the EC2 Instance Control section to enable the upload.
                  </Typography>

                  <div
                    align="center"
                    style={{ display: "flex", justifyContent: "center", paddingTop: "0px" }}
                  >
                    <Form style={{ marginBottom: "0", marginTop: "20px" }}>
                      <InputGroup style={{ marginBottom: "0" }}>
                        <Input
                          key={inputKey}
                          type="file"
                          multiple
                          onChange={(e) => setSelectedFiles(Array.from(e.target.files))}
                          style={{ minWidth: "400px", height: "38px" }}
                          disabled={uploading}
                        />
                        <Button
                          color="outline-secondary"
                          onClick={() => handleUploadMultiple(false)}
                          disabled={uploading || !ec2IsRunning || scriptStateLower !== "running"}
                          style={{
                            backgroundColor: "#eaf9ea",
                            color: "grey",
                            fontSize: "15px",
                            marginLeft: "10px",
                            marginRight: "10px",
                            height: "38px",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "0 16px",
                          }}
                        >
                          {uploading ? "Uploading..." : "Upload"}
                        </Button>
                        <Button
                          color="outline-secondary"
                          onClick={() => handleCancelUpload(false)}
                          disabled={!uploading}
                          style={{
                            fontSize: "15px",
                            height: "38px",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "0 16px",
                          }}
                        >
                          Cancel Upload
                        </Button>
                      </InputGroup>
                    </Form>
                  </div>

                  {!waitingForInference && uploadedFiles.length > 0 && showFileList && (
                    <LoadImagePreview
                      mode="upload"
                      organization={organization}
                      fileList={uploadedFiles}
                      uploadDatePrefix={uploadDatePrefix}
                      getPresignedUrl={getPresignedUrlAPI}
                      containerWidth={400}
                      containerHeight={300}
                      showZoomControls
                      wheelZoomMode="ctrlKey"
                    />
                  )}

                  {waitingForInference && (
                    <div style={{ marginTop: "20px", textAlign: "center" }}>
                      <Typography variant="body2">{inferenceStatusMessage}</Typography>
                      <CircularProgress size={20} style={{ marginTop: "10px" }} />
                    </div>
                  )}
                </>
              )}
            </ComponentCard>
          </Col>
        </Row>
      </div>



      {/* Archived Inference Preview */}
      <div align="center" style={{ paddingTop: "0px" }}>
        <Row className="justify-content-center" style={{ margin: "0 auto", maxWidth: "1200px" }}>
          <Col sm="12" md="12">
            <ComponentCard>
              <Typography variant="body1" sx={{ fontWeight: "bold", marginBottom: "10px" }}>
                Archived Inference Preview
              </Typography>

              {/* Date pickers + Search */}
              <Row style={{ marginTop: "20px" }}>
                <Col sm="2"></Col>
                <Col sm="8">
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: "20px",
                      flexWrap: "wrap",
                    }}
                  >
                    <DatePicker
                      selected={startDate}
                      onChange={(date) => setStartDate(date)}
                      dateFormat="yyyy-MM-dd"
                      isClearable
                      placeholderText="Start Date"
                      style={{ height: "30px", fontSize: "13px" }}
                    />
                    <DatePicker
                      selected={endDate}
                      onChange={(date) => setEndDate(date)}
                      dateFormat="yyyy-MM-dd"
                      isClearable
                      placeholderText="End Date"
                      style={{ height: "30px", fontSize: "13px" }}
                    />
                    <Button
                      size="sm"
                      onClick={handleArchiveSearch}
                      style={{ height: "30px", fontSize: "13px" }}
                    >
                      Search
                    </Button>
                  </div>
                </Col>
                <Col sm="2"></Col>
              </Row>

              {/* Conditional render based on search state */}
              {!archiveSearched ? (
                <Typography
                  variant="body2"
                  sx={{ textAlign: "center", color: "gray", marginTop: "24px" }}
                >
                  Select a date range to view inference results.
                </Typography>
              ) : archiveLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                  <CircularProgress size={22} />
                </Box>
              ) : filePairs.length > 0 ? (
                <LoadImagePreview
                  mode="archive"
                  organization={organization}
                  filePairs={filePairs}
                  getPresignedUrl={getPresignedUrlAPI}
                  containerWidth={400}
                  containerHeight={300}
                  showZoomControls={true}
                />
              ) : (
                <Typography
                  variant="body2"
                  sx={{ textAlign: "center", color: "gray", marginTop: "24px" }}
                >
                  No files found for the selected date range.
                </Typography>
              )}
            </ComponentCard>
          </Col>
        </Row>
      </div>
    </Box>
  );
};

export default ModelTestingComp;